import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import {
    clearDisplayProfileCookies,
    DISPLAY_COOKIE_NAMES,
    fetchAuthMe,
    logout as logoutApi,
    setDisplayProfileCookies,
} from './LoginUtils';

const AuthContext = createContext(null);

/**
 * Session auth via HttpOnly cookie; profile fields mirrored in display cookies for UI.
 */
export function AuthProvider({ children }) {
    const [cookies, setCookie, removeCookie] = useCookies(DISPLAY_COOKIE_NAMES);
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const refreshAuth = useCallback(async ({ showLoading = true } = {}) => {
        if (showLoading) {
            setIsLoading(true);
        }
        try {
            const me = await fetchAuthMe();
            if (me) {
                setUser(me);
                setDisplayProfileCookies(setCookie, me);
            } else {
                setUser(null);
                clearDisplayProfileCookies(removeCookie);
            }
            return me;
        } catch (err) {
            console.error('Failed to refresh auth:', err);
            setUser(null);
            clearDisplayProfileCookies(removeCookie);
            return null;
        } finally {
            if (showLoading) {
                setIsLoading(false);
            }
        }
    }, [setCookie, removeCookie]);

    /** Revalidate session without toggling global loading state. */
    const silentRefreshAuth = useCallback(
        () => refreshAuth({ showLoading: false }),
        [refreshAuth]
    );

    useEffect(() => {
        refreshAuth();
    }, [refreshAuth]);

    const setAuthenticatedUser = useCallback(
        (profile) => {
            if (!profile) {
                setUser(null);
                clearDisplayProfileCookies(removeCookie);
                return;
            }
            setUser(profile);
            setDisplayProfileCookies(setCookie, profile);
        },
        [setCookie, removeCookie]
    );

    const logout = useCallback(
        async (renderLoginButton) => {
            await logoutApi(removeCookie, renderLoginButton);
            setUser(null);
        },
        [removeCookie]
    );

    const value = useMemo(
        () => ({
            user,
            isAuthenticated: !!user,
            isLoading,
            cookies,
            refreshAuth,
            silentRefreshAuth,
            setAuthenticatedUser,
            logout,
            setCookie,
            removeCookie,
        }),
        [user, isLoading, cookies, refreshAuth, silentRefreshAuth, setAuthenticatedUser, logout, setCookie, removeCookie]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return ctx;
}
